import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';
import { useLanguage } from '../../context/LanguageContext';

const AIRBNB_URL = 'https://www.airbnb.fr/rooms/1645617357744906325';

const IMAGES = [
  '/photo-site/siderite/04DBDCE6-B7FD-4EC7-B377-DC11C8851257.JPG',
  '/photo-site/siderite/3B6C94D3-AA1B-4CD2-8B55-AD8E73DB86C0.JPG',
  '/photo-site/siderite/662BC4D9-7F85-40C0-BAE3-BDA75094E5BE.JPG',
  '/photo-site/siderite/AC150037-BC3D-4C11-B15A-3B3F4D220077.JPG',
  '/photo-site/siderite/IMG_7064.jpeg',
  '/photo-site/siderite/IMG_7260.jpeg',
  '/photo-site/siderite/IMG_7261.jpeg',
  '/photo-site/siderite/IMG_7262.jpeg',
  '/photo-site/siderite/IMG_7263.jpeg',
  '/photo-site/siderite/IMG_7266.jpeg',
  '/photo-site/siderite/IMG_7267.jpeg',
  '/photo-site/siderite/IMG_7268.jpeg',
  '/photo-site/siderite/IMG_7269.jpeg',
  '/photo-site/siderite/IMG_7270.jpeg',
];

const AppartementSiderite = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mainImage, setMainImage] = useState(IMAGES[0]);
  const equipements = t('siderite.equip');

  return (
    <>
    <SEO
      title={t('seo.siderite.title')}
      description={t('seo.siderite.description')}
      keywords="appartement siderite la grave, location 4 personnes la grave, plain-pied vue glacier, hébergement oisans hautes-alpes"
      url="/appartements/appartement-4-personnes-siderite"
      image="https://marmottejoyeuse.fr/photo-site/siderite/04DBDCE6-B7FD-4EC7-B377-DC11C8851257.JPG"
    />
    <div className="container mx-auto px-4 pt-28 pb-16 min-h-screen">
      <button
        onClick={() => navigate('/appartements')}
        className="fixed top-24 left-4 md:left-8 z-10 btn btn-circle btn-ghost bg-base-200/50 backdrop-blur-sm hover:bg-base-200"
        aria-label={t('siderite.back')}
      >
        ←
      </button>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <span className="text-primary font-semibold text-sm">{t('siderite.label')}</span>
          <h1 className="text-4xl font-bold mt-1">{t('siderite.name')}</h1>
          <p className="text-base-content/60 mt-1">{t('siderite.tagline')}</p>
        </div>

        <div className="mb-10 bg-base-200 rounded-xl p-4">
          <img
            src={mainImage}
            alt="Appartement Sidérite - La Marmotte Joyeuse"
            className="w-full h-[400px] object-cover rounded-lg mb-4 transition-all duration-300"
          />
          <div className="flex gap-3 overflow-x-auto py-1">
            {IMAGES.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Vue ${i + 1}`}
                onClick={() => setMainImage(img)}
                className={`w-28 h-20 object-cover rounded-lg cursor-pointer flex-shrink-0 transition-all duration-300
                  ${mainImage === img ? 'ring-2 ring-primary ring-offset-2' : 'hover:opacity-80'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 mb-10">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('siderite.descTitle')}</h2>
              <p className="text-base-content/80 leading-relaxed">{t('siderite.desc')}</p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('siderite.equipTitle')}</h2>
              <ul className="space-y-2">
                {Array.isArray(equipements) && equipements.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-base-content/80">
                    <span className="text-primary mt-1 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('siderite.infoTitle')}</h2>
            <div className="bg-base-200 p-6 rounded-xl space-y-5">
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('siderite.capacity')}</h3>
                <p>{t('siderite.capacityVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('siderite.surface')}</h3>
                <p>{t('siderite.surfaceVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('siderite.rate')}</h3>
                <p className="text-2xl font-bold">{t('siderite.rateVal')} <span className="text-base font-normal text-base-content/60">{t('siderite.perNight')}</span></p>
              </div>
              <a
                href={AIRBNB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full gap-2"
              >
                {t('siderite.bookAirbnb')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AppartementSiderite;
